if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Check for a checked addon checkbox within this product's section
        const productSection = this.closest('[id^="ProductInfo-"]');
        const addonCheckbox = productSection?.querySelector('.product-addon-checkbox__input:checked');
        const addonVariantId = addonCheckbox ? parseInt(addonCheckbox.dataset.addonVariantId, 10) : null;

        // KEET-507 — gather add-on tracking context at submit time (same point as addonVariantId)
        // so identical values feed both the cart-add line-item properties and the
        // `addon_added_to_cart` analytics event below. Every read is guarded; a tracking failure
        // must never block the add to cart.
        let addonProps = null;
        let addonEventData = null;
        if (addonVariantId && addonCheckbox) {
          try {
            const gaClientId = (document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/) || [])[1] || null;
            const parentProductId = this.closest('[data-product-id]')?.dataset.productId || null;
            const parentVariantId = formData.get('id') || null;
            const addonProductId = addonCheckbox.dataset.addonProductId || null;

            // Title/price come from the AddonVariants JSON the add-on snippet renders —
            // structured data, so no fragile money-string parsing.
            let addonVariant = null;
            const addonDataEl = productSection?.querySelector('script[id^="AddonVariants-"]');
            if (addonDataEl) {
              try {
                const addonVariants = JSON.parse(addonDataEl.textContent);
                if (Array.isArray(addonVariants)) {
                  addonVariant = addonVariants.find((v) => v.id === addonVariantId) || null;
                }
              } catch (parseErr) {
                console.error('KEET-507 AddonVariants parse failed', parseErr);
              }
            }

            addonProps = { _addon: 'true', _addon_source: 'pdp-addon-checkbox' };
            if (parentProductId) addonProps._addon_parent_product_id = parentProductId;
            if (parentVariantId) addonProps._addon_parent_variant_id = parentVariantId;
            if (gaClientId) addonProps._ga_client_id = gaClientId;

            addonEventData = {
              addon_product_id: addonProductId,
              addon_variant_id: addonVariantId,
              addon_variant_title: addonVariant ? addonVariant.title : null,
              addon_price: addonVariant ? addonVariant.price / 100 : null,
              currency: window.Shopify?.currency?.active || 'USD',
              addon_parent_product_id: parentProductId,
              addon_parent_variant_id: parentVariantId,
              client_id: gaClientId,
            };
          } catch (trackErr) {
            console.error('KEET-507 add-on tracking setup failed', trackErr);
            addonProps = null;
            addonEventData = null;
          }
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // If addon checkbox is checked, add the addon before rendering the cart.
            //
            // KEET-546 — the sections bundled onto the first /cart/add were rendered against a
            // cart that did not hold the add-on yet, so the cart-icon-bubble HTML in that
            // response is one item short. Refetch the sections once the add-on has landed and
            // hand renderContents that HTML instead, so the drawer body and the header count
            // both paint the final cart state on first paint. Stock Dawn's onCartUpdate() does
            // not cover this: it refreshes cart-drawer-items and .cart-drawer__footer only,
            // never #cart-icon-bubble.
            //
            // The refetch runs on both settle paths on purpose. A third-party fetch wrapper on
            // this store consumes the add-on response body (see the KEET-507 note below), which
            // diverts this chain to .catch even though the add itself succeeded. Reading the
            // live cart is right either way — if the add genuinely failed, the refetched
            // sections describe a cart without the add-on, which is the truth. The settle
            // handler is deliberately quiet rather than logging: on this store the wrapper
            // makes .catch the normal path, so an error log here would fire on every add.
            if (addonVariantId && !this.error) {
              const addonItem = { id: addonVariantId, quantity: 1 };
              if (addonProps) addonItem.properties = addonProps;
              const keetsaSettle = () => this.keetsaFetchCartSections(response);

              return fetch(`${routes.cart_add_url}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({
                  items: [addonItem],
                }),
              })
                .then((r) => r.json())
                .then(keetsaSettle)
                .catch(keetsaSettle);
            }

            return response;
          })
          .then((response) => {
            if (!response || this.error) return;

            // KEET-507 — publish Event 1 here rather than in the add-on fetch's own .then.
            // window.fetch is wrapped by a third-party script on this store that consumes the
            // add-on /cart/add response, diverting that nested chain to .catch (so the publish
            // never ran there). This outer .then runs reliably once the cart add resolves, and
            // addonVariantId is already in scope here (used by the Rebuy refresh below).
            if (addonVariantId && addonEventData) {
              try {
                if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
                  window.Shopify.analytics.publish('addon_added_to_cart', addonEventData);
                }
              } catch (publishErr) {
                console.error('[KEET-507] addon_added_to_cart publish failed', publishErr);
              }
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
              if (addonVariantId) {
                setTimeout(() => { try { window.Rebuy?.Cart?.fetch(); } catch(e) {} });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /**
       * KEET-546 — re-render the cart sections against the cart as it stands right now.
       *
       * Bundled section rendering on /cart/add reflects the cart at the moment of that request,
       * so it is stale whenever a second add follows. This re-renders the same section ids
       * through the Section Rendering API (GET ?sections=a,b — up to five sections, returns a
       * JSON map of section id to HTML) against the current page path, matching the
       * sections_url sent on the original request.
       *
       * @param {Object} response Parsed /cart/add response to merge the fresh sections into.
       * @returns {Promise<Object>} `response` carrying fresh section HTML, or `response`
       *   unchanged when the refetch fails — a section-render hiccup must never break the add
       *   to cart, and the stale sections it falls back to are what shipped before this fix.
       */
      keetsaFetchCartSections(response) {
        const sectionIds = this.cart
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);

        // Encode each id but keep a literal comma separator — the API splits the raw value.
        const sectionsParam = sectionIds.map((sectionId) => encodeURIComponent(sectionId)).join(',');
        const url = `${window.location.pathname}?sections=${sectionsParam}`;

        // No Accept header on purpose. `Accept: application/json` on a product URL makes Shopify
        // content-negotiate to the product JSON ({"product":{...}}) and ignore `sections`
        // altogether, which would silently leave the stale sections in place. Verified against
        // /products/tea-leaf-dream on 2026-08-21. Dawn's own section fetches send no Accept
        // override either.
        return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
          .then((sectionsResponse) => {
            // Also the guard for the API's five-section cap, which answers 400.
            if (!sectionsResponse.ok) throw new Error(`Cart section refresh failed: ${sectionsResponse.status}`);
            return sectionsResponse.json();
          })
          .then((sections) => {
            if (!sections || typeof sections !== 'object') return response;

            // A section whose Liquid failed comes back null at HTTP 200, so keep only usable
            // HTML and merge over the original sections rather than replacing them wholesale.
            const freshSections = {};
            sectionIds.forEach((sectionId) => {
              const html = sections[sectionId];
              if (typeof html === 'string' && html.length > 0) freshSections[sectionId] = html;
            });

            return { ...response, sections: { ...response.sections, ...freshSections } };
          })
          .catch((error) => {
            console.error(error);
            return response;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
